import { useState } from 'react';
import { Search, Loader2, Check, AlertCircle, Database, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { useSearchIndexStatus, useMigrateBookmarks } from '@/hooks';

interface SemanticSearchStatusProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SemanticSearchStatus({ trigger, open: controlledOpen, onOpenChange }: SemanticSearchStatusProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;
  const { data: status, isLoading, refetch } = useSearchIndexStatus();
  const migrateMutation = useMigrateBookmarks();

  const handleMigrate = async () => {
    try {
      const result = await migrateMutation.mutateAsync();
      if (result.failed > 0) {
        toast.warning(`${result.success}개 성공, ${result.failed}개 실패`);
      } else {
        toast.success(`${result.success}개 북마크 인덱싱 완료`);
      }
      refetch();
    } catch (error: any) {
      toast.error(error.message || '인덱싱 중 오류가 발생했습니다');
    }
  };

  const progress = status?.indexing_progress ?? 0;
  const isFullyIndexed = status && status.indexed_bookmarks === status.total_bookmarks;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Only render trigger in uncontrolled mode */}
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm">
              <Search className="w-4 h-4 mr-1.5" />
              의미 검색
              {isFullyIndexed && <Check className="w-3 h-3 ml-1 text-green-500" />}
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            의미 검색 설정
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Status Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">인덱싱 상태</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoading}
                className="h-7 px-2"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : status ? (
              <div className="space-y-3">
                <Progress value={progress} className="h-2" />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {status.indexed_bookmarks} / {status.total_bookmarks} 북마크
                  </span>
                  <span className={progress === 100 ? 'text-green-600' : 'text-amber-600'}>
                    {progress}%
                  </span>
                </div>

                {status.pending_bookmarks > 0 && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded-md">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{status.pending_bookmarks}개 북마크가 인덱싱되지 않았습니다</span>
                  </div>
                )}

                {isFullyIndexed && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-950/30 p-2 rounded-md">
                    <Check className="w-4 h-4 shrink-0" />
                    <span>모든 북마크가 인덱싱되었습니다</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-4">
                상태를 불러올 수 없습니다
              </div>
            )}
          </div>

          {/* Migration Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-2">북마크 인덱싱</h4>
            <p className="text-xs text-muted-foreground mb-3">
              기존 북마크에 임베딩을 생성하여 의미 검색이 가능하게 합니다.
              새로 추가되는 북마크는 자동으로 인덱싱됩니다.
            </p>
            <Button
              onClick={handleMigrate}
              disabled={migrateMutation.isPending || isFullyIndexed}
              className="w-full"
            >
              {migrateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  인덱싱 중...
                </>
              ) : isFullyIndexed ? (
                <>
                  <Check className="w-4 h-4 mr-1.5" />
                  인덱싱 완료
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-1.5" />
                  {status?.pending_bookmarks ? `${status.pending_bookmarks}개 인덱싱` : '전체 인덱싱'}
                </>
              )}
            </Button>
          </div>

          {/* Info Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-2">의미 검색이란?</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• 키워드가 아닌 의미를 기반으로 검색합니다</li>
              <li>• "프론트엔드" 검색 시 React, Vue 관련 북마크도 찾습니다</li>
              <li>• 북마크가 많을수록 검색 품질이 향상됩니다</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
